import React, { useState, useEffect } from 'react';
import { getAllSessions, getGameSession } from '../utils/gameApi';
import { isSuperAdmin, getBranchId, fetchWithAuth } from '../utils/auth';
import { API_URL } from '../config';
import './AdminSessionList.css';

function AdminSessionList({ onSessionSelected }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const superAdmin = isSuperAdmin();
  const myBranchId = getBranchId();
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(superAdmin ? '' : myBranchId);

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => {
    loadSessions();
    // Auto-refresh every 5 seconds
    const interval = setInterval(() => {
      loadSessions(true);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const loadSessions = async (isRefresh = false) => {
    if (!isRefresh) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');

    try {
      const data = await getAllSessions();
      setSessions(data.sessions || []);
      setLoading(false);
      setRefreshing(false);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setError('Failed to load sessions');
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSessionClick = async (sessionCode) => {
    try {
      const sessionData = await getGameSession(sessionCode);
      // Transform to match control panel expectations
      const sessionForControl = {
        code: sessionCode,
        joinUrl: `http://localhost:3000/#game-join`,
        qrCode: null, // Will be generated on control panel if needed
        ...sessionData
      };
      onSessionSelected(sessionForControl);
    } catch (error) {
      console.error('Error loading session:', error);
      setError('Failed to load session details');
    }
  };

  const getStateColor = (state) => {
    switch (state) {
      case 'waiting': return 'state-waiting';
      case 'question': return 'state-question';
      case 'results': return 'state-results';
      case 'finished': return 'state-finished';
      default: return '';
    }
  };

  const getStateLabel = (state) => {
    return state.replace('_', ' ').toUpperCase();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;

    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry - now;

    if (diffMs < 0) return 'Expired';

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 24) {
      const days = Math.floor(diffHours / 24);
      const hours = diffHours % 24;
      return `${days}d ${hours}h remaining`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMins}m remaining`;
    } else {
      return `${diffMins}m remaining`;
    }
  };

  if (loading) {
    return (
      <div className="admin-session-list-container">
        <div className="loading">Loading sessions...</div>
      </div>
    );
  }

  return (
    <div className="admin-session-list-container">
      <div className="sessions-header">
        <h1>Active Game Sessions</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {superAdmin && branches.length > 0 && (
            <select
              value={selectedBranchId}
              onChange={e => setSelectedBranchId(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px' }}
            >
              <option value="">All Branches</option>
              {branches.map(b => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
            </select>
          )}
          <button
            className="refresh-button"
            onClick={() => loadSessions()}
            disabled={refreshing}
          >
            {refreshing ? '⟳ Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {(() => {
        const filtered = selectedBranchId
          ? sessions.filter(s => String(s.branchId) === String(selectedBranchId))
          : sessions;
        return filtered.length === 0 ? (
          <div className="no-sessions">
            <div className="no-sessions-icon">🎮</div>
            <h2>No Active Sessions</h2>
            <p>Create a new game session to get started!</p>
          </div>
        ) : (
          <div className="sessions-grid">
            {filtered.map((session) => (
            <div
              key={session.sessionCode}
              className="session-card"
              onClick={() => handleSessionClick(session.sessionCode)}
            >
              <div className="session-card-header">
                <div className="session-code">{session.sessionCode}</div>
                <div className={`session-state ${getStateColor(session.currentState)}`}>
                  {getStateLabel(session.currentState)}
                </div>
              </div>

              <div className="session-card-body">
                <h3 className="session-title">{session.questionGroupName}</h3>

                <div className="session-stats">
                  <div className="stat">
                    <span className="stat-icon">👥</span>
                    <span className="stat-value">{session.participantCount}</span>
                    <span className="stat-label">Participants</span>
                  </div>

                  <div className="stat">
                    <span className="stat-icon">❓</span>
                    <span className="stat-value">
                      {session.currentQuestionIndex + 1}/{session.totalQuestions}
                    </span>
                    <span className="stat-label">Questions</span>
                  </div>
                </div>

                <div className="session-info-footer">
                  <div className="session-created">
                    Created: {formatDate(session.createdAt)}
                  </div>
                  {session.expiresAt && (
                    <div className="session-expiry">
                      ⏱ {getTimeRemaining(session.expiresAt)}
                    </div>
                  )}
                </div>
              </div>

              <div className="session-card-footer">
                <span className="manage-link">Manage Session →</span>
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      {refreshing && (
        <div className="refresh-indicator">
          Auto-refreshing...
        </div>
      )}
    </div>
  );
}

export default AdminSessionList;
